import { Typography } from '@mui/material'
import SettingSwitch from 'components/atoms/input/SettingSwitch'
import OverlayLoading from 'components/atoms/OverlayLoading'
import RequestRetryAlert from 'components/atoms/RequestRetryAlert'
import { useEffect, useState } from 'react'
import { CONSTANTS_OPTIONS, NOTIFICATION_TYPES } from 'services/constants'
import { getConstant } from 'services/constantServices'
import { updateStates } from 'services/userServices'
import { useTranslation } from 'react-i18next'
import { GridRow } from 'components/molecules/user/SettingsMolecules'
import { ColStack } from 'components/atoms/container/InCardStack'

const NotificationSettings = ({ userStates }) => {
  const { t } = useTranslation()
  const [configurations, setConfigurations] = useState([])
  const [initialSettings, setInitialSettings] = useState({})
  const [toggled, setToggled] = useState(false)
  const [userSettings, setUserSettings] = useState({})
  const [updateError, setUpdateError] = useState(undefined)
  const [loading, setLoading] = useState(false)

  const initializeByConfig = config => {
    const setting = {}
    setting[NOTIFICATION_TYPES.EMAIL] = true
    if (!config.emailOnly) {
      setting[NOTIFICATION_TYPES.PUSH] = true
    }
    return setting
  }

  useEffect(() => {
    const fetchData = async () => {
      const configConstant = await getConstant(
        CONSTANTS_OPTIONS.NOTIFICATION_CONFIG
      )
      const settingsInit = {}
      if (configConstant) {
        configConstant.forEach(config => {
          settingsInit[config.settingKey] = initializeByConfig(config)
        })
        setInitialSettings(settingsInit)
        setUserSettings(userStates.notificationSettings || settingsInit)
        setConfigurations(configConstant)
      }
      setLoading(false)
    }
    setLoading(true)
    if (userStates) {
      fetchData()
    }
  }, [userStates])

  useEffect(() => {
    const updateSettings = async () => {
      const requestBody = {
        notificationSettings: JSON.stringify(userSettings)
      }
      const res = await updateStates(requestBody)
      if (res.status !== 200) {
        setUpdateError(true)
      } else {
        setUpdateError(false)
      }
    }

    if (toggled) {
      updateSettings()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSettings])

  const handleIndividualChange = (e, type, settingName) => {
    setToggled(true)
    const newSettings = { ...userSettings }
    if (!newSettings[settingName])
      newSettings[settingName] = initialSettings[settingName]
    newSettings[settingName][type] = e.target.checked
    setUserSettings(newSettings)
  }

  const ConfigRow = ({ config }) => (
    <ColStack spacing={4}>
      <Typography variant='h5'>{config.label}</Typography>
      <GridRow
        col1Element={
          <Typography variant='body1Small' color='textSecondary'>
            {config.description}
          </Typography>
        }
        col2Element={
          <SettingSwitch
            checked={Boolean(
              userSettings[config.settingKey]
                ? userSettings[config.settingKey][NOTIFICATION_TYPES.EMAIL]
                : true
            )}
            onChange={e =>
              handleIndividualChange(
                e,
                NOTIFICATION_TYPES.EMAIL,
                config.settingKey
              )
            }
            testId={`switch-${config.settingKey}-email`}
          />
        }
        col3Element={
          <>
            {!config.emailOnly && (
              <SettingSwitch
                checked={Boolean(
                  userSettings[config.settingKey]
                    ? userSettings[config.settingKey][NOTIFICATION_TYPES.PUSH]
                    : true
                )}
                onChange={e =>
                  handleIndividualChange(
                    e,
                    NOTIFICATION_TYPES.PUSH,
                    config.settingKey
                  )
                }
                testId={`switch-${config.settingKey}-push`}
              />
            )}
          </>
        }
      />
    </ColStack>
  )

  return (
    <ColStack spacing={12}>
      <Typography variant='h3'>{t('settings.notifications')}</Typography>

      <GridRow
        col1Element={
          <Typography variant='eyebrow'>{t('settings.type')}</Typography>
        }
        col2Element={
          <Typography variant='eyebrow'>{t('overview.email')}</Typography>
        }
        col3Element={
          <Typography variant='eyebrow'>{t('settings.inApp')}</Typography>
        }
      />

      {configurations.map(config => (
        <ConfigRow key={config.settingKey} config={config} />
      ))}

      {updateError ? <RequestRetryAlert /> : null}
      <OverlayLoading open={loading} />
    </ColStack>
  )
}

export default NotificationSettings
